import { useState } from 'react';
import type { ReactNode } from 'react';
import { Automation } from '@/types/entities';
import { EntityDrawer } from '@/components/ui/EntityDrawer';
import { useI18n } from '@/i18n';
import { AGENT_DEPARTMENTS } from '@/config/agentDepartments.config';

export interface AutomationFormValues {
  name: string;
  description: string;
  trigger: string;
  conditions: string;
  actions: string;
  ownerAgent: string;
  approvalLevel: 1 | 2 | 3;
}

function toValues(a?: Automation | null, defaultApprovalLevel: 1 | 2 | 3 = 2): AutomationFormValues {
  return {
    name: a?.name ?? '',
    description: a?.description ?? '',
    trigger: a?.trigger ?? '',
    conditions: a?.conditions ?? '',
    actions: a?.actions ?? '',
    ownerAgent: a?.ownerAgent ?? AGENT_DEPARTMENTS[0].department,
    approvalLevel: a?.approvalLevel ?? defaultApprovalLevel,
  };
}

export function AutomationFormDrawer({
  automation,
  defaultApprovalLevel,
  onClose,
  onSubmit,
}: {
  automation?: Automation | null;
  defaultApprovalLevel?: 1 | 2 | 3;
  onClose: () => void;
  onSubmit: (v: AutomationFormValues) => void;
}) {
  const { t } = useI18n();
  const [values, setValues] = useState<AutomationFormValues>(toValues(automation, defaultApprovalLevel));
  const [error, setError] = useState('');

  function set<K extends keyof AutomationFormValues>(key: K, val: AutomationFormValues[K]) {
    setValues((v) => ({ ...v, [key]: val }));
  }

  return (
    <EntityDrawer title={automation ? t('automationForm.editTitle') : t('automationForm.newTitle')} onClose={onClose}>
      <Field label={t('automationForm.name')} required><input className="text-input" value={values.name} onChange={(e) => set('name', e.target.value)} /></Field>
      <Field label={t('automationForm.description')}><textarea className="text-input" rows={2} value={values.description} onChange={(e) => set('description', e.target.value)} /></Field>
      <Field label={t('automationForm.trigger')} required><input className="text-input" value={values.trigger} onChange={(e) => set('trigger', e.target.value)} /></Field>
      <Field label={t('automationForm.conditions')}><input className="text-input" value={values.conditions} onChange={(e) => set('conditions', e.target.value)} /></Field>
      <Field label={t('automationForm.actions')} required><textarea className="text-input" rows={2} value={values.actions} onChange={(e) => set('actions', e.target.value)} /></Field>
      <Field label={t('automationForm.ownerAgent')}>
        <select className="text-input" value={values.ownerAgent} onChange={(e) => set('ownerAgent', e.target.value)}>
          {AGENT_DEPARTMENTS.map((d) => <option key={d.department} value={d.department}>{t(d.nameKey)}</option>)}
        </select>
      </Field>
      <Field label={t('automationForm.approvalLevel')}>
        <select className="text-input" value={values.approvalLevel} onChange={(e) => set('approvalLevel', Number(e.target.value) as 1 | 2 | 3)}>
          <option value={1}>{t('permission.level1')}</option>
          <option value={2}>{t('permission.level2')}</option>
          <option value={3}>{t('permission.level3')}</option>
        </select>
      </Field>

      {error && <div style={{ fontSize: 12, color: 'var(--crit)', marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn-primary"
          onClick={() => (values.name.trim() && values.trigger.trim() && values.actions.trim() ? onSubmit(values) : setError(t('automationForm.required')))}
        >
          {t('common.save')}
        </button>
        <button onClick={onClose}>{t('common.cancel')}</button>
      </div>
    </EntityDrawer>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{label}{required && <span style={{ color: 'var(--crit)' }}> *</span>}</label>
      {children}
    </div>
  );
}
